import { demoSeeding, DemoError } from "./demo.js";
import { Refusal } from "./franchises.js";
import { anonKey, supabase, url } from "./supabase.js";
import type {
  MySeeding,
  PoolSeeding,
  SeededPlayer,
  SeedingEntry,
  SeedingList,
  SeedingPanelist,
} from "./types.js";

/**
 * The code `close_seeding` raises when a panelist has not submitted an up-to-date list. The
 * panel asks whether to close anyway instead of just showing the message. See 20261001001100.
 */
export const SEEDING_INCOMPLETE = "AF002";

interface WhoRow {
  display_name: string;
  avatar_url: string | null;
}

interface PanelistRow {
  member_id: string;
  members: WhoRow | null;
}

interface RankRow {
  panelist_id: string;
  member_id: string;
  /** Null for a player the panelist skipped. */
  position: number | null;
  submitted_at: string | null;
  members: WhoRow | null;
}

/**
 * What `seeding_average` returns and `seeding_results` holds. Numerics may arrive as text, and
 * are null for a player no counted list ranked.
 */
interface AverageRow {
  member_id: string;
  mean: number | string | null;
  median: number | string | null;
  best: number | null;
  worst: number | null;
  ranked: number;
  lists: number;
  panel: number;
  rank_order: number;
}

interface ResultRow extends AverageRow {
  members: WhoRow | null;
}

interface MyRow {
  member_id: string;
  display_name: string;
  avatar_url: string | null;
  place: number;
  is_new: boolean;
  skipped: boolean;
  submitted: boolean;
  out_of_date: boolean;
}

const SOMEONE = "Someone";

function refuse(error: { message: string; code?: string | null }): never {
  throw new Refusal(error.message, error.code ?? null);
}

export function toSeedingEntry(row: AverageRow, who: WhoRow | null): SeedingEntry {
  return {
    memberId: row.member_id,
    displayName: who?.display_name ?? SOMEONE,
    avatarUrl: who?.avatar_url ?? null,
    mean: row.mean === null ? null : Number(row.mean),
    median: row.median === null ? null : Number(row.median),
    best: row.best,
    worst: row.worst,
    ranked: row.ranked,
    lists: row.lists,
    panel: row.panel,
    order: row.rank_order,
  };
}

/**
 * Every panelist's list for a pool, panelists with nothing saved included. A row with no
 * position is a player the panelist skipped. A list is submitted when its rows carry a
 * `submitted_at`; the database writes a list whole, so they all do or none.
 */
export function toSeedingLists(panelists: PanelistRow[], ranks: RankRow[]): SeedingList[] {
  return panelists
    .map((p): SeedingList => {
      const panelist: SeedingPanelist = {
        memberId: p.member_id,
        displayName: p.members?.display_name ?? SOMEONE,
        avatarUrl: p.members?.avatar_url ?? null,
      };
      const mine = ranks.filter((r) => r.panelist_id === p.member_id);
      const who = (r: RankRow): SeedingPanelist => ({
        memberId: r.member_id,
        displayName: r.members?.display_name ?? SOMEONE,
        avatarUrl: r.members?.avatar_url ?? null,
      });
      const players: SeededPlayer[] = mine
        .flatMap((r) => (r.position === null ? [] : [{ ...who(r), place: r.position }]))
        .sort((a, b) => a.place - b.place);
      const skipped = mine
        .filter((r) => r.position === null)
        .map(who)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      return {
        panelist,
        submitted: mine.length > 0 && mine.every((r) => r.submitted_at !== null),
        players,
        skipped,
      };
    })
    .sort((a, b) => a.panelist.displayName.localeCompare(b.panelist.displayName));
}

export function toMySeeding(rows: MyRow[]): MySeeding {
  const players = rows
    .map((r) => ({
      memberId: r.member_id,
      displayName: r.display_name,
      avatarUrl: r.avatar_url,
      place: r.place,
      // A player with no saved row has not been placed: on a fresh list that is everybody, and
      // on a submitted one it is whoever registered since.
      state: r.is_new
        ? ("unplaced" as const)
        : r.skipped
          ? ("skipped" as const)
          : ("ranked" as const),
    }))
    .sort((a, b) => a.place - b.place);
  return {
    players,
    submitted: rows[0]?.submitted ?? false,
    outOfDate: rows[0]?.out_of_date ?? false,
  };
}

/**
 * A pool's seeding as an organiser sees it: the panel, every list, and the average as it stands.
 * Anybody else gets nothing back for the lists and the average, because the database sends
 * nothing — so this shows an empty panel to a member, which is true as far as they are told.
 */
export async function fetchPoolSeeding(eventId: string): Promise<PoolSeeding> {
  if (!supabase) return demoSeeding(eventId);

  const [panelResult, rankResult, averageResult] = await Promise.all([
    supabase
      .from("seeding_panelists")
      // `members!member_id`, because `seeding_ranks` also joins a panelist to members, and
      // without the hint PostgREST finds two ways to embed and answers 300.
      .select("member_id, members!member_id (display_name, avatar_url)")
      .eq("event_id", eventId),
    supabase
      .from("seeding_ranks")
      .select(
        "panelist_id, member_id, position, submitted_at, members!member_id (display_name, avatar_url)",
      )
      .eq("event_id", eventId),
    supabase.rpc("seeding_average", { target_event: eventId }),
  ]);

  if (panelResult.error) refuse(panelResult.error);
  if (rankResult.error) refuse(rankResult.error);
  if (averageResult.error) refuse(averageResult.error);

  const panelists = panelResult.data as unknown as PanelistRow[];
  const ranks = rankResult.data as unknown as RankRow[];

  // The average names members by id; their names are already in the rows above.
  const names = new Map<string, WhoRow | null>();
  for (const r of ranks) names.set(r.member_id, r.members);

  return {
    lists: toSeedingLists(panelists, ranks),
    average: (averageResult.data as unknown as AverageRow[])
      .map((row) => toSeedingEntry(row, names.get(row.member_id) ?? null))
      .sort((a, b) => a.order - b.order),
  };
}

/**
 * The average as it was fixed when seeding closed, for organisers. Empty while seeding is open,
 * and for anybody who is not an organiser.
 */
export async function fetchPublishedSeeding(eventId: string): Promise<SeedingEntry[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("seeding_results")
    .select(
      "member_id, mean, median, best, worst, ranked, lists, panel, rank_order, members (display_name, avatar_url)",
    )
    .eq("event_id", eventId)
    .order("rank_order", { ascending: true });

  if (error) refuse(error);
  return (data as unknown as ResultRow[]).map((row) => toSeedingEntry(row, row.members));
}

/** The caller's own list for a pool, brought up to date with who is registered. */
export async function fetchMySeeding(eventId: string): Promise<MySeeding> {
  if (!supabase) return { players: [], submitted: false, outOfDate: false };

  const { data, error } = await supabase.rpc("my_seeding", { target_event: eventId });
  if (error) refuse(error);
  return toMySeeding(data as unknown as MyRow[]);
}

/**
 * The pools a member is on the seeding panel for. Filtered by member, because an organiser can
 * read the whole panel and this is asking about one person.
 */
export async function fetchPanelEventIds(memberId: string): Promise<string[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("seeding_panelists")
    .select("event_id")
    .eq("member_id", memberId);

  if (error) refuse(error);
  return (data ?? []).map((row) => row.event_id as string);
}

export async function appointPanelist(eventId: string, memberId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("appoint_panelist", {
    target_event: eventId,
    target_member: memberId,
  });
  if (error) refuse(error);
}

export async function removePanelist(eventId: string, memberId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("remove_panelist", {
    target_event: eventId,
    target_member: memberId,
  });
  if (error) refuse(error);
}

/**
 * Save the caller's list: `ranked` in the order given, `skipped` skipped. Without `submit` it is
 * a draft, which may leave players out and counts for nothing; with it the list must rank at
 * least one player and rank or skip everybody registered.
 */
export async function saveSeeding(
  eventId: string,
  ranked: string[],
  submit: boolean,
  skipped: string[] = [],
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("save_seeding", {
    target_event: eventId,
    member_ids: ranked,
    submit,
    skipped_ids: skipped,
  });
  if (error) refuse(error);
}

// The signed-in member's access token, kept to hand for `saveSeedingDraftOnLeave`, which cannot
// wait to be given one.
let accessToken: string | null = null;
supabase?.auth.onAuthStateChange((_event, session) => {
  accessToken = session?.access_token ?? null;
});

/**
 * `saveSeeding` for a draft, as the page goes away. A page that is being closed or reloaded runs
 * nothing that is awaited, and cancels its requests, so this builds the request without waiting
 * for anything and sends it with `keepalive`, which the browser finishes after the page is gone.
 * Nothing comes back to report: the page will not be there to hear it.
 */
export function saveSeedingDraftOnLeave(eventId: string, ranked: string[], skipped: string[]) {
  if (!url || !anonKey || accessToken === null) return;

  void fetch(`${url}/rest/v1/rpc/save_seeding`, {
    method: "POST",
    keepalive: true,
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      target_event: eventId,
      member_ids: ranked,
      submit: false,
      skipped_ids: skipped,
    }),
  }).catch(() => undefined);
}

/**
 * Close seeding and fix the average. Refused with `SEEDING_INCOMPLETE` while a panelist has
 * no up-to-date list, unless `anyway` is set.
 */
export async function closeSeeding(eventId: string, anyway = false): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("close_seeding", { target_event: eventId, anyway });
  if (error) refuse(error);
}

export async function reopenSeeding(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("reopen_seeding", { target_event: eventId });
  if (error) refuse(error);
}
